// Single-row editor for the characters table on the producer's character
// editor page. Stays controlled (parent owns edits via the mutation hook) so
// the totals header at the top of the page can reactively recompute the
// project budget.

import { copy } from '@ohear/shared'
import { Badge, Button, Input, Select } from '@ohear/ui'
import type { CharacterRow, ContractStatus, PickupMode } from '../../lib/api/producer-types'
import { useT } from '../../lib/i18n/use-t'

const PICKUP_MODE_OPTIONS: Array<{ value: PickupMode; label: string }> = [
  { value: 'per_pickup', label: 'Per pickup' },
  { value: 'per_session', label: 'Per session' },
  { value: 'flat_included', label: 'Flat (included)' },
]

const CONTRACT_STATUS_VARIANT: Record<ContractStatus, 'gray' | 'blue' | 'green' | 'amber' | 'red'> =
  {
    draft: 'gray',
    sent: 'amber',
    signed: 'blue',
    active: 'green',
    completed: 'green',
    voided: 'red',
  }

export interface CharacterRowEditorProps {
  row: CharacterRow
  /** All actors in the org plus an "Invite new" option, supplied by parent. */
  actorOptions: Array<{ id: string; label: string }>
  onChange: (patch: Partial<CharacterRow>) => void
  onAssignActor: (actorId: string | null) => void
  onInviteActor: () => void
  onSendForSigning: () => void
  busySend?: boolean
}

export function CharacterRowEditor({
  row,
  actorOptions,
  onChange,
  onAssignActor,
  onInviteActor,
  onSendForSigning,
  busySend,
}: CharacterRowEditorProps) {
  const t = useT()
  const derivedPickup = derivePickupRate(row)

  return (
    <tr className="border-t border-[var(--border)] align-top">
      <td className="px-3 py-2 text-[13px] font-mono text-[var(--text)]">{row.character_code}</td>
      <td className="px-3 py-2 text-[13px] text-[var(--text)]">
        <Input
          aria-label={`Display name for ${row.character_code}`}
          value={row.display_name}
          onChange={(e) => onChange({ display_name: e.target.value })}
          className="h-8"
        />
      </td>
      <td className="px-3 py-2">
        <Select
          aria-label={`Assigned actor for ${row.character_code}`}
          value={row.assigned_actor_id ?? '__none__'}
          onChange={(e) => {
            const v = e.target.value
            if (v === '__invite__') onInviteActor()
            else onAssignActor(v === '__none__' ? null : v)
          }}
          className="h-8"
        >
          <option value="__none__">Unassigned</option>
          {actorOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label}
            </option>
          ))}
          <option value="__invite__">{t(copy.common.PLACEHOLDERS.invite_new_actor)}</option>
        </Select>
      </td>
      <td className="px-3 py-2 text-right text-[13px] tabular-nums text-[var(--text)]">
        {row.line_count}
      </td>
      <td className="px-3 py-2">
        <Input
          aria-label={`Per-line rate for ${row.character_code}`}
          type="number"
          inputMode="decimal"
          min={0}
          step={0.01}
          leftAddon="$"
          value={row.line_rate_usd}
          onChange={(e) => onChange({ line_rate_usd: Number.parseFloat(e.target.value) || 0 })}
          className="h-8"
        />
      </td>
      <td className="px-3 py-2">
        <Select
          aria-label={`Pickup mode for ${row.character_code}`}
          value={row.pickup_mode}
          onChange={(e) => onChange({ pickup_mode: e.target.value as PickupMode })}
          className="h-8"
        >
          {PICKUP_MODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-3 py-2 text-right text-[13px] tabular-nums text-[var(--text-2)]">
        {row.pickup_mode === 'flat_included' ? (
          <span title="Pickups are included in the flat line rate">included</span>
        ) : (
          formatUsd(derivedPickup)
        )}
      </td>
      <td className="px-3 py-2">
        <Badge variant={CONTRACT_STATUS_VARIANT[row.contract_status]}>{row.contract_status}</Badge>
      </td>
      <td className="px-3 py-2">
        <Button
          size="sm"
          variant={row.contract_status === 'draft' ? 'primary' : 'ghost'}
          loading={busySend}
          disabled={row.contract_status !== 'draft' || !row.assigned_actor_id}
          onClick={onSendForSigning}
        >
          Send for signing
        </Button>
      </td>
    </tr>
  )
}

function derivePickupRate(row: CharacterRow): number {
  if (row.pickup_mode === 'flat_included') return 0
  return row.pickup_rate_usd
}

function formatUsd(value: number | null | undefined): string {
  // Financials can be absent before the first contract lands — render an
  // honest em-dash instead of "$NaN" (Intl happily formats undefined to NaN).
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}
